import {
  KSendSuccessResponse,
  PersistedFilterSelectionsDto,
  PersistedFilterSelectionsDtoResponse,
} from '@asset-metrix/portal-gateway-client-js';
import { MutationFunction, QueryFunction } from '@tanstack/react-query';
import { v5 as uuidv5 } from 'uuid';

import { APPLICATION_UUID } from '../../config';
import { useStore } from '../../store';
import {
  QueryKeyWithRole,
  unwrapQueryKeyWithRole,
} from '../../utils/prependRoleId';
import { portalGatewayClient } from '../portalGatewayClient';

export type PageFilterPersistenceQueryKey = readonly [
  { reference: string; global: boolean },
];
const PAGE_FILTER_PERSISTENCE_KIND = 'page';
const PAGE_FILTER_PERSISTENCE_SET_KEY = 'default';

function normalizePersistenceReference(reference: string): string {
  const { tenantName } = useStore.getState();

  if (tenantName == null) {
    throw new Error(
      'Tenant name is required to normalize persistence reference'
    );
  }

  const namespace = uuidv5(tenantName, APPLICATION_UUID);
  return uuidv5(reference, namespace);
}

export const fetchPageFilterPersistence: QueryFunction<
  PersistedFilterSelectionsDtoResponse | null,
  QueryKeyWithRole<PageFilterPersistenceQueryKey>
> = async ({ queryKey }) => {
  const [{ reference, global }] = unwrapQueryKeyWithRole(queryKey).params;
  const normalizedReference = normalizePersistenceReference(reference);
  const result =
    await portalGatewayClient.customization.getPersistedFilterSelections(
      PAGE_FILTER_PERSISTENCE_KIND,
      normalizedReference,
      PAGE_FILTER_PERSISTENCE_SET_KEY,
      { global }
    );
  return result.data;
};

export const mutatePageFilterPersistence: MutationFunction<
  KSendSuccessResponse,
  { reference: string; data: PersistedFilterSelectionsDto; global: boolean }
> = async ({ reference, data, global }) => {
  const normalizedReference = normalizePersistenceReference(reference);
  const response =
    await portalGatewayClient.customization.putPersistedFilterSelections(
      PAGE_FILTER_PERSISTENCE_KIND,
      normalizedReference,
      PAGE_FILTER_PERSISTENCE_SET_KEY,
      data,
      { global }
    );
  return response.data;
};

export const deletePageFilterPersistence: MutationFunction<
  KSendSuccessResponse,
  { reference: string; global: boolean }
> = async ({ reference, global }) => {
  const normalizedReference = normalizePersistenceReference(reference);
  const response =
    await portalGatewayClient.customization.deletePersistedFilterSelections(
      PAGE_FILTER_PERSISTENCE_KIND,
      normalizedReference,
      PAGE_FILTER_PERSISTENCE_SET_KEY,
      { global }
    );
  return response.data;
};
